import {
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  InputAdornment,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputLabel,
} from "@mui/material";
import { CustomInput } from "global/components";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import strings from "global/constants/StringConstants";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import { useState } from "react";
import { addSingleGroup } from "screens/Contacts/ContactsService";

interface CustomProps {
  isVisible: boolean;
  handleDialogClose: Function;
  filters: any;
  setFilters: Function;
}

const AddGroupModal = (props: CustomProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fields, setFields] = useState({
    groupName: {
      value: "",
      error: "",
    },
    recipientCount: {
      value: 0,
      error: "",
    },
    groupType: {
      value: 0,
      error: "",
    },
  });

  const handleOnChange = (event: React.ChangeEvent<any>) => {
    if (strings.groupNameRegex.test(event.target.value))
      setFields({
        ...fields,
        [event.target.name]: {
          value: event.target.value,
          error: "",
        },
      });
  };

  const handleValidation = () => {
    let isValid = true;
    const groupName = fields.groupName.value.trim();
    if (!groupName) {
      setFields({
        ...fields,
        groupName: {
          value: "",
          error: "Please enter group name",
        },
      });
      isValid = false;
    }
    return isValid;
  };

  const addSingleGroupHandler = async () => {
    try {
      if (handleValidation()) {
        setIsLoading(true);
        const body = {
          name: fields.groupName.value.trim(),
          recipientCount: fields.recipientCount.value,
          public: fields.groupType.value,
        };
        const res = await addSingleGroup(body);
        openSuccessNotification(res.message);
        setIsLoading(false);
        props.handleDialogClose();
        props.setFilters({
          ...props.filters,
          page: 1,
        });
      }
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getDialogBody = () => {
    return (
      <Stack spacing={3}>
        <Stack spacing={2}>
          <Box>
            <CustomInput
              required
              id="groupname-input"
              label="Group Name"
              placeHolder="Group name"
              name="groupName"
              value={fields.groupName.value}
              onChange={handleOnChange}
              propsToInputElement={{ minLength: 1, maxLength: 50 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ fontSize: "0.75rem" }}>
                      {fields.groupName.value?.length} / 50
                    </span>
                  </InputAdornment>
                ),
              }}
              error={
                !isTruthy(fields.groupName.value) && fields.groupName.error
              }
              helperText={
                !isTruthy(fields.groupName.value) ? fields.groupName.error : ""
              }
            />
          </Box>
          <Box my={1}>
            <InputLabel
              sx={{
                color: "#475569",
                fontWeight: 600,
                fontSize: "14px",
                marginLeft: "5px",
              }}
              required
            >
              Group Type
            </InputLabel>
          </Box>
          <RadioGroup
            name="groupType"
            value={fields.groupType.value}
            onChange={(event: any) => {
              setFields({
                ...fields,
                [event.target.name]: {
                  value: +event.target.value,
                  error: "",
                },
              });
            }}
          >
            <Stack spacing={2}>
              <FormControlLabel
                value={0}
                control={<Radio size="small" />}
                label={
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Private Group
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contacts added to a private group will be visible only to
                      you and your managers.
                    </Typography>
                  </Stack>
                }
              />
              <FormControlLabel
                value={1}
                control={<Radio size="small" />}
                label={
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Public Group
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contacts added to a public group will be accessible to
                      everyone under your account.
                    </Typography>
                  </Stack>
                }
              />
            </Stack>
          </RadioGroup>
        </Stack>
      </Stack>
    );
  };

  const getFooter = () => {
    return (
      <Stack direction="row" justifyContent="center" spacing={2}>
        <Button
          id="contacts_add__cancel_btn"
          variant="outlined"
          onClick={() => props.handleDialogClose()}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Cancel
        </Button>
        <Button
          id="contacts_add_save_btn"
          variant="contained"
          onClick={() => addSingleGroupHandler()}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Save
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <CustomLoader isLoading={isLoading} />
      <CustomDialog
        handleDialogClose={() => props.handleDialogClose()}
        isDialogOpen={props.isVisible}
        body={getDialogBody()}
        footer={getFooter()}
        title={
          <Typography variant="h4" color="#7551D0">
            Create a group
          </Typography>
        }
        closeButtonVisibility={true}
        closable
        maxWidth="sm"
      />
    </>
  );
};

export default AddGroupModal;
